import React from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerClose,
} from '@frontend/shadcn/components/ui/drawer';
import { XmarkIcon } from '@revfluence/fresh-icons/solid';
import { Button } from '@frontend/shadcn/components/ui/button';
import { cn } from '@frontend/shadcn/lib/utils';
import { P } from '@frontend/shadcn/components/typography/p';
import { FileContractIcon, PenToSquareIcon } from '@revfluence/fresh-icons/regular/esm';
import { useApolloClient } from '@apollo/client';
import GET_BUDGET_PERIOD_DETAILS from '@frontend/app/queries/getBudgetPeriodDetailsQuery';
import { useUpdateBriefsFiscalPeriodMutation } from '@frontend/app/hooks';
import { logger } from '@common';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { useBriefs } from './context/BriefsContext';
import { BriefConnectionsList } from './components/BriefConnectionsList';

interface ProjectState {
  fiscalYear?: string;
  isExpanded: boolean;
  isSelected: boolean;
  periods: Array<{
    key: string;
    id: string;
    label: string;
    granularityLabelForPayload: string;
  }>;
}

export const EditBriefDrawer: React.FC = React.memo(() => {
  const client = useApolloClient();
  const [
    updateBriefsFiscalPeriod,
    { loading: updateBriefsFiscalPeriodLoading },
  ] = useUpdateBriefsFiscalPeriodMutation();
  const { isEditBriefDrawerOpen, setIsEditBriefDrawerOpen, selectedRows, setRowSelection, fetchBriefs } = useBriefs();
  const [projectStates, setProjectStates] = React.useState<Record<number, ProjectState>>({});

  // Memoize project states with periods for sorting
  const projectStatesWithPeriods = React.useMemo(() => {
    return Object.entries(projectStates).reduce((acc, [id, state]) => {
      if (state.periods?.length > 0) {
        acc[Number(id)] = true;
      }
      return acc;
    }, {} as Record<number, boolean>);
  }, [projectStates]);

  // Group briefs by project - only depends on selectedRows
  const projectGroups = React.useMemo(() => {
    const groups = selectedRows.reduce(
      (acc, brief) => {
        const projectId = brief.project?.id;
        const projectName = brief.project?.name;

        if (!projectId || !projectName) return acc;

        const existingGroup = acc.find((group) => group.projectId === projectId);
        if (existingGroup) {
          existingGroup.briefs.push(brief);
          existingGroup.totalAmount += brief.payment || 0;
        } else {
          acc.push({
            projectId,
            projectName,
            briefs: [brief],
            totalAmount: brief.payment || 0,
          });
        }
        return acc;
      },
      [] as Array<{
        projectId: number;
        projectName: string;
        briefs: typeof selectedRows;
        totalAmount: number;
      }>,
    );

    // Sort groups: projects with fiscal periods first, then by name
    return groups.sort((a, b) => {
      const aHasPeriods = projectStatesWithPeriods[a.projectId];
      const bHasPeriods = projectStatesWithPeriods[b.projectId];

      if (aHasPeriods !== bHasPeriods) {
        return bHasPeriods ? 1 : -1;
      }

      return a.projectName.localeCompare(b.projectName);
    });
  }, [selectedRows, projectStatesWithPeriods]);

  // Memoize project IDs to prevent unnecessary effect runs
  const projectIds = React.useMemo(() => selectedRows.map((brief) => brief.project?.id), [selectedRows]);

  // Fetch budget periods for each project only when drawer opens
  React.useEffect(() => {
    const fetchProjectPeriods = async () => {
      await Promise.all(
        projectIds.map(async (projectId) => {
          try {
            const { data } = await client.query({
              query: GET_BUDGET_PERIOD_DETAILS,
              variables: {
                budgetAccountId: null,
                programId: projectId,
              },
              fetchPolicy: 'no-cache',
            });

            if (data.periodDetails?.length) {
              const periods = data.periodDetails.flatMap((period) => {
                const result = [];
                result.push({
                  key: `FY${period.fiscalYearPeriodDefinitionId}`,
                  id: period.fiscalYearPeriodDefinitionId,
                  label: period.fiscalYearLabel,
                  granularityLabelForPayload: 'FY',
                });
                if (period.quarterDistributions?.length) {
                  period.quarterDistributions.forEach((quarter) => {
                    result.push({
                      key: quarter.quarterKey + quarter.quarterPeriodDefinitionId,
                      id: quarter.quarterPeriodDefinitionId,
                      label: quarter.quarterLabel,
                      granularityLabelForPayload: quarter.quarterKey,
                    });
                  });
                }
                return result;
              });

              setProjectStates((prev) => ({
                ...prev,
                [projectId]: {
                  ...prev[projectId],
                  periods,
                  isExpanded: prev[projectId]?.isExpanded || false,
                  isSelected: prev[projectId]?.isSelected ?? periods.length > 0,
                  fiscalYear: prev[projectId]?.fiscalYear,
                },
              }));
            }
          } catch (error) {
            console.error(`Error fetching periods for project ${projectId}:`, error);
          }
        }),
      );
    };

    if (isEditBriefDrawerOpen && projectIds.length > 0) {
      fetchProjectPeriods();
    }
  }, [client, projectIds, isEditBriefDrawerOpen]);

  // Memoize the handleClose callback
  const handleClose = React.useCallback(() => {
    setIsEditBriefDrawerOpen(false);
    setRowSelection({});
    setProjectStates({});
  }, [setIsEditBriefDrawerOpen, setRowSelection]);

  // Memoize the toggleProject callback
  const toggleProject = React.useCallback((projectId: number) => {
    setProjectStates((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        isExpanded: !prev[projectId]?.isExpanded,
      },
    }));
  }, []);

  // Memoize the updateProjectState callback
  const updateProjectState = React.useCallback((projectId: number, updates: Partial<ProjectState>) => {
    setProjectStates((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        ...updates,
      },
    }));
  }, []);

  // Memoize the toggleProjectSelection callback
  const toggleProjectSelection = React.useCallback((projectId: number) => {
    setProjectStates((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        isSelected: !prev[projectId]?.isSelected,
      },
    }));
  }, []);

  // Memoize the handleUpdateBriefsFiscalPeriod callback
  const handleUpdateBriefsFiscalPeriod = React.useCallback(() => {
    if (!selectedRows.length) {
      console.error('No briefs selected');
      return;
    }

    // Get all selected project states
    const selectedProjectStates = Object.entries(projectStates)
      .filter(([_, state]) => state.isSelected)
      .map(([projectId, state]) => ({
        projectId: Number(projectId),
        state
      }));

    if (!selectedProjectStates.length) {
      console.error('No projects selected');
      return;
    }

    // Group briefs by selected projects
    const briefsByProject = selectedProjectStates.reduce((acc, { projectId }) => {
      const projectBriefs = selectedRows.filter((brief) => brief.project?.id === projectId);
      if (projectBriefs.length) {
        acc[projectId] = projectBriefs;
      }
      return acc;
    }, {} as Record<number, typeof selectedRows>);

    if (!Object.keys(briefsByProject).length) {
      console.error('No briefs found for selected projects');
      return;
    }

    // Create input for each selected project
    const briefFiscalPeriodLinkInput = selectedProjectStates.map(({ projectId, state }) => {
      const projectBriefs = briefsByProject[projectId];
      if (!projectBriefs?.length || !state.periods?.length) {
        return null;
      }

      // Find the period that matches the fiscalYear using the key
      const selectedPeriod = state.periods.find((period) => period.key === state.fiscalYear);
      if (!selectedPeriod) {
        console.error(`No period found matching fiscal year ${state.fiscalYear} for project ${projectId}`);
        return null;
      }

      return {
        agreementIds: projectBriefs.map((brief) => brief.id),
        budgetPeriodDefinitionId: selectedPeriod.id,
        fiscalGranularity: selectedPeriod.granularityLabelForPayload === 'FY' ? 'YEAR' : 'QUARTER',
        fiscalGranularityLabel: selectedPeriod.granularityLabelForPayload
      };
    }).filter(Boolean);

    if (!briefFiscalPeriodLinkInput.length) {
      console.error('No valid fiscal period inputs generated');
      return;
    }

    updateBriefsFiscalPeriod({
      variables: {
        data: {
          briefFiscalPeriodLinkInput
        }
      },
      onError: (error) => {
        logger.error('Error updating briefs fiscal period:', error);
        handleClose();
      },
      onCompleted: () => {
        handleClose();
        fetchBriefs();
      }
    });
  }, [updateBriefsFiscalPeriod, selectedRows, projectStates, handleClose, fetchBriefs]);

  // Memoize the validation error message
  const getValidationError = React.useMemo(() => {
    if (!selectedRows.length) {
      return 'No briefs selected';
    }

    const selectedProjectStates = Object.entries(projectStates)
      .filter(([_, state]) => state.isSelected)
      .map(([projectId, state]) => ({
        projectId: Number(projectId),
        state
      }));

    if (!selectedProjectStates.length) {
      return 'Please select at least one project to update';
    }

    const briefsByProject = selectedProjectStates.reduce((acc, { projectId }) => {
      const projectBriefs = selectedRows.filter((brief) => brief.project?.id === projectId);
      if (projectBriefs.length) {
        acc[projectId] = projectBriefs;
      }
      return acc;
    }, {} as Record<number, typeof selectedRows>);

    if (!Object.keys(briefsByProject).length) {
      return 'No briefs found for selected projects';
    }

    // Check if all selected projects have fiscal years
    const projectsWithoutFiscalYear = selectedProjectStates
      .filter(({ state }) => !state.fiscalYear || !state.periods?.length);

    if (projectsWithoutFiscalYear.length > 0) {
      return 'Please select a fiscal year for all selected projects';
    }

    // Validate fiscal year selections for all projects
    const invalidProjects = selectedProjectStates
      .filter(({ projectId, state }) => {
        const projectBriefs = briefsByProject[projectId];
        if (!projectBriefs?.length) return true;

        const selectedPeriod = state.periods.find((period) => period.key === state.fiscalYear);
        return !selectedPeriod;
      });

    if (invalidProjects.length > 0) {
      return 'Please select a valid fiscal year for all selected projects';
    }

    return null;
  }, [selectedRows, projectStates]);

  console.log('projectStates', projectStates);
  return (
    <Drawer
      open={isEditBriefDrawerOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      onClose={handleClose}
      direction="right"
    >
      <DrawerContent
        className={cn(
          'fixed left-auto inset-y-0 mt-0 right-0 z-[9999] flex h-full w-[572px] flex-col border-l bg-background rounded-none p-0',
          'duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          'bg-background backdrop-blur-sm',
          'top-[var(--header-height,0px)] h-[calc(100vh-var(--header-height,0px))]',
          'bg-white',
        )}
        data-vaul-no-drag
      >
        <DrawerHeader className="flex justify-between items-center border-b border-grey-3 px-6 py-4">
          <DrawerTitle className="font-medium mb-0">Update Briefs in Bulk</DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="headerIcon"
              className="border border-transparent hover:border-grey-6 hover:bg-transparent"
              onClick={handleClose}
            >
              <XmarkIcon className="h-4 w-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="flex flex-col overflow-y-scroll w-full h-full">
          {/* Top header */}
          <div className="flex items-center gap-2 bg-grey-1 px-4 min-h-[48px] border-b border-grey-3">
            <div className="flex items-center justify-center h-8 w-8 bg-grey-2 rounded-2xl">
              <PenToSquareIcon className="h-3 w-3" />
            </div>
            <P>{selectedRows.length} Briefs Selected</P>
          </div>
          {/* Main content */}
          <div className="flex flex-col gap-4 p-4">
            <section className="flex flex-col p-4 border rounded-xl bg-grey-1">
              <div className="flex flex-row gap-2 items-center">
                <FileContractIcon className="w-4 h-4 text-grey-5" />
                <P className="text-sm font-medium text-grey-5">Total Brief Amount for selected Briefs</P>
              </div>
              <P className="text-3xl font-normal">
                {`$${selectedRows.reduce((sum, brief) => sum + (brief.payment || 0), 0).toLocaleString()}`}
              </P>
            </section>
            <section className="flex flex-col gap-2">
              <P className="text-sm font-medium">Connections</P>
              <BriefConnectionsList
                projectGroups={projectGroups}
                projectStates={projectStates}
                onToggleProject={toggleProject}
                onUpdateProjectState={updateProjectState}
                onToggleSelection={toggleProjectSelection}
              />
            </section>
          </div>
        </div>

        <DrawerFooter className="flex flex-row gap-2 space-x-2 border-t border-grey-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    disabled={updateBriefsFiscalPeriodLoading || !!getValidationError}
                    onClick={handleUpdateBriefsFiscalPeriod}
                  >
                    Update Briefs
                  </Button>
                </span>
              </TooltipTrigger>
              {getValidationError && (
                <TooltipContent>{getValidationError}</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <Button variant="outline" onClick={handleClose} disabled={updateBriefsFiscalPeriodLoading}>
            Cancel
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
});
